import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { BrandLockup } from '../components/BrandLogo';
import { SiteFooter } from '../components/SiteFooter';
import { de } from '../i18n/de';
import { IMPRINT, PRIVACY, type LegalBlock } from '../lib/legal';
import { APP_VERSION } from '../lib/version';

/** Rahmen der Infoseiten (öffentlich, angemeldet wie abgemeldet): oben Logo und Name zur Startseite, der Titel, der Inhalt, die Fußzeile. */
function InfoFrame({ title, testId, children }: { title: string; testId: string; children?: ReactNode }) {
  return (
    <div className="home info-page" data-testid={testId}>
      <div className="home-wrap">
        <header className="home-head">
          <Link to="/" className="landing-brand" aria-label={de.app.name}>
            <BrandLockup />
            <span className="beta-pill">{de.landing.beta}</span>
          </Link>
        </header>
        <main>
          <section className="home-intro">
            <div>
              <h1>{title}</h1>
              {children}
            </div>
          </section>
        </main>
      </div>
      <SiteFooter />
    </div>
  );
}

/** „Über“: vorerst nur die Version (aus dem Wurzel-package.json, siehe RELEASING.md) */
export function AboutPage() {
  return (
    <InfoFrame title={de.info.aboutTitle} testId="about-page">
      <p className="home-summary" data-testid="app-version">
        {de.info.version(APP_VERSION)}
      </p>
    </InfoFrame>
  );
}

/** Zeilen mit Umbruch dazwischen (wie Anschrift und Register im Impressum) */
const Lines = ({ lines }: { lines: readonly string[] }) => (
  <>
    {lines.map((l, i) => (
      <Fragment key={l}>
        {i > 0 && <br />}
        {l}
      </Fragment>
    ))}
  </>
);

/** Impressum: Anbieter, Register, Vertretung, Kontakt (E-Mail und Telefon als Links) und die weiteren Pflichtangaben aus lib/legal */
export function ImprintPage() {
  const I = IMPRINT;
  return (
    <InfoFrame title={de.info.imprintTitle} testId="imprint-page">
      <div className="legal" data-testid="imprint">
        <address>
          <strong>{I.company}</strong>
          <br />
          <Lines lines={I.address} />
        </address>
        <p>
          <Lines lines={I.register} />
        </p>
        <p>
          <strong>{I.representedBy.label}</strong>
          <br />
          <Lines lines={I.representedBy.names} />
        </p>
        <section>
          <h2>{I.contact.title}</h2>
          <p>
            {I.contact.phone.label}: <a href={I.contact.phone.href}>{I.contact.phone.display}</a>
            <br />
            {I.contact.email.label}: <a href={`mailto:${I.contact.email.address}`}>{I.contact.email.address}</a>
          </p>
        </section>
        {I.sections.map((s) => (
          <section key={s.title}>
            <h2>{s.title}</h2>
            <p>
              {s.label && (
                <>
                  <strong>{s.label}</strong>
                  <br />
                </>
              )}
              <Lines lines={s.lines} />
            </p>
          </section>
        ))}
      </div>
    </InfoFrame>
  );
}

/** Text mit Webadressen als Links (ein Satzzeichen direkt dahinter gehört nicht zur Adresse) */
function Linked({ text }: { text: string }) {
  const parts = text.split(/(https?:\/\/\S+?)(?=[.,;:)]?(?:\s|$))/);
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <a key={i} href={part} target="_blank" rel="noopener noreferrer">
            {part}
          </a>
        ) : (
          part
        ),
      )}
    </>
  );
}

/** Verantwortliche Stelle bzw. Anbieter: Anschrift und Kontakt (aus dem Impressum) */
function Responsible() {
  const I = IMPRINT;
  return (
    <>
      <address>
        {I.company}
        <br />
        <Lines lines={I.address} />
      </address>
      <p>
        {I.contact.phone.label}: <a href={I.contact.phone.href}>{I.contact.phone.display}</a>
        <br />
        {I.contact.email.label}: <a href={`mailto:${I.contact.email.address}`}>{I.contact.email.address}</a>
      </p>
    </>
  );
}

function Block({ block }: { block: LegalBlock }) {
  if ('h2' in block) return <h2>{block.h2}</h2>;
  if ('h3' in block) return <h3>{block.h3}</h3>;
  if ('h4' in block) return <h4>{block.h4}</h4>;
  if ('ul' in block)
    return (
      <ul>
        {block.ul.map((item) => (
          <li key={item}>
            <Linked text={item} />
          </li>
        ))}
      </ul>
    );
  if ('responsible' in block) return <Responsible />;
  return (
    <p>
      <Linked text={block.p} />
    </p>
  );
}

/** Datenschutzerklärung: Überschriften, Absätze und Aufzählungen aus lib/legal (Webadressen werden Links) */
export function PrivacyPage() {
  return (
    <InfoFrame title={de.info.privacyTitle} testId="privacy-page">
      <div className="legal privacy" data-testid="privacy">
        {PRIVACY.map((block, i) => (
          <Block key={i} block={block} />
        ))}
      </div>
    </InfoFrame>
  );
}
